import { useEffect, useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { DateTime } from 'luxon'
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@smorgrav/velpr-ui'
import { apiFetch } from '@/api'
import {
  AccountTier,
  type AccountDoc,
  type UsageDoc
} from '@smorgrav/hundehall_shared'
import {
  useCustomerOverviews,
  type CustomerOverview
} from '@/hooks/useCustomerOverviews'

type SortKey =
  | 'name'
  | 'tier'
  | 'orders'
  | 'revenue'
  | 'lastOrder'
  | 'lastPayment'

export const Route = createFileRoute('/customers')({
  component: CustomersPage
})

interface BackfillResult {
  siteId: string
  status: string
  lastPaymentAt?: string
  lastPaymentAmount?: number
  message?: string
}

function CustomersPage() {
  const { overviews, loading, refetch } = useCustomerOverviews()
  const [tierFilter, setTierFilter] = useState<AccountTier | 'all'>('all')
  const [sortKey, setSortKey] = useState<SortKey>('revenue')
  const [selected, setSelected] = useState<CustomerOverview | null>(null)
  const [backfilling, setBackfilling] = useState(false)
  const [backfillReport, setBackfillReport] = useState<BackfillResult[] | null>(
    null
  )

  // Keep the open dialog's data in sync with refreshed overviews (e.g. after
  // saving an edit) so the user sees the updated values.
  useEffect(() => {
    if (!selected) return
    const fresh = overviews.find((o) => o.site.id === selected.site.id)
    if (fresh && fresh !== selected) setSelected(fresh)
  }, [overviews, selected])

  const runBackfill = async () => {
    if (
      !confirm(
        'Hent siste betaling fra Stripe for alle stripe-kunder? Dette kan ta noen sekunder.'
      )
    )
      return
    setBackfilling(true)
    setBackfillReport(null)
    try {
      const res = await apiFetch('/admin/stripe/backfill-payments', {
        method: 'POST'
      })
      const json = await res.json()
      if (!res.ok || !json.success) {
        throw new Error(json.error || `HTTP ${res.status}`)
      }
      setBackfillReport(json.results as BackfillResult[])
    } catch (err) {
      console.error('Backfill failed', err)
      alert(
        `Backfill feilet: ${err instanceof Error ? err.message : String(err)}`
      )
    } finally {
      setBackfilling(false)
    }
  }

  const filtered = useMemo(() => {
    const rows =
      tierFilter === 'all'
        ? overviews
        : overviews.filter((o) => o.account?.plan.tier === tierFilter)
    const sorted = [...rows].sort((a, b) => {
      switch (sortKey) {
        case 'name':
          return a.site.name.localeCompare(b.site.name)
        case 'tier':
          return (a.account?.plan.tier || '').localeCompare(
            b.account?.plan.tier || ''
          )
        case 'orders':
          return (
            (b.currentUsage?.orders.completed.count || 0) -
            (a.currentUsage?.orders.completed.count || 0)
          )
        case 'revenue':
          return (
            (b.currentUsage?.orders.completed.amount || 0) -
            (a.currentUsage?.orders.completed.amount || 0)
          )
        case 'lastOrder':
          return (b.currentUsage?.lastOrderDate || '').localeCompare(
            a.currentUsage?.lastOrderDate || ''
          )
        case 'lastPayment':
          return (b.account?.plan.lastPaymentAt || '').localeCompare(
            a.account?.plan.lastPaymentAt || ''
          )
      }
    })
    return sorted
  }, [overviews, tierFilter, sortKey])

  const totals = useMemo(() => {
    return overviews.reduce(
      (acc, o) => {
        const tier = o.account?.plan.tier
        if (tier) acc.byTier[tier] = (acc.byTier[tier] || 0) + 1
        acc.ordersThisMonth += o.currentUsage?.orders.completed.count || 0
        acc.revenueThisMonth += o.currentUsage?.orders.completed.amount || 0
        return acc
      },
      {
        byTier: {} as Record<string, number>,
        ordersThisMonth: 0,
        revenueThisMonth: 0
      }
    )
  }, [overviews])

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Kunder</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Oversikt over abonnementer og bruk per anlegg.
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="text-muted-foreground text-right text-sm">
            <div>{overviews.length} anlegg</div>
            <div>
              {totals.ordersThisMonth} ordre ·{' '}
              {fmtAmount(totals.revenueThisMonth)} denne måneden
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={runBackfill}
            disabled={backfilling}
          >
            {backfilling ? 'Henter fra Stripe...' : 'Hent siste betaling fra Stripe'}
          </Button>
        </div>
      </div>

      {backfillReport && (
        <BackfillReport
          results={backfillReport}
          onClose={() => setBackfillReport(null)}
        />
      )}

      {/* Tier filter */}
      <div className="flex flex-wrap gap-2">
        <FilterChip
          active={tierFilter === 'all'}
          onClick={() => setTierFilter('all')}
        >
          Alle ({overviews.length})
        </FilterChip>
        {(Object.values(AccountTier) as AccountTier[]).map((tier) => (
          <FilterChip
            key={tier}
            active={tierFilter === tier}
            onClick={() => setTierFilter(tier)}
          >
            {tier} ({totals.byTier[tier] || 0})
          </FilterChip>
        ))}
      </div>

      {loading ? (
        <p className="text-muted-foreground text-sm">Laster kundedata...</p>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-muted-foreground text-xs uppercase">
              <tr>
                <SortHeader
                  active={sortKey === 'name'}
                  onClick={() => setSortKey('name')}
                >
                  Anlegg
                </SortHeader>
                <SortHeader
                  active={sortKey === 'tier'}
                  onClick={() => setSortKey('tier')}
                >
                  Plan
                </SortHeader>
                <th className="px-3 py-2 text-left">Status</th>
                <SortHeader
                  active={sortKey === 'orders'}
                  onClick={() => setSortKey('orders')}
                  align="right"
                >
                  Ordre nå
                </SortHeader>
                <SortHeader
                  active={sortKey === 'revenue'}
                  onClick={() => setSortKey('revenue')}
                  align="right"
                >
                  Omsetning nå
                </SortHeader>
                <SortHeader
                  active={sortKey === 'lastOrder'}
                  onClick={() => setSortKey('lastOrder')}
                >
                  Siste ordre
                </SortHeader>
                <SortHeader
                  active={sortKey === 'lastPayment'}
                  onClick={() => setSortKey('lastPayment')}
                >
                  Siste betaling
                </SortHeader>
              </tr>
            </thead>
            <tbody>
              {filtered.map((o) => (
                <tr
                  key={o.site.id}
                  onClick={() => setSelected(o)}
                  className="hover:bg-muted/30 cursor-pointer border-t"
                >
                  <td className="px-3 py-2">
                    <div className="font-medium">{o.site.name}</div>
                    <div className="text-muted-foreground text-xs">
                      {o.site.id}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <TierBadge tier={o.account?.plan.tier} />
                    {o.account?.plan.paymentProvider && (
                      <div className="text-muted-foreground mt-0.5 text-xs">
                        {o.account.plan.paymentProvider}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <PlanStatus account={o.account} />
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {o.currentUsage?.orders.completed.count ?? '–'}
                    {o.previousUsage && (
                      <div className="text-muted-foreground text-xs">
                        forrige: {o.previousUsage.orders.completed.count}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {fmtAmount(o.currentUsage?.orders.completed.amount)}
                    {o.previousUsage && (
                      <div className="text-muted-foreground text-xs">
                        forrige:{' '}
                        {fmtAmount(o.previousUsage.orders.completed.amount)}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {fmtDate(o.currentUsage?.lastOrderDate)}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {o.account?.plan.lastPaymentAt ? (
                      <>
                        <div>{fmtDate(o.account.plan.lastPaymentAt)}</div>
                        {o.account.plan.lastPaymentAmount > 0 && (
                          <div className="text-muted-foreground text-xs">
                            {fmtAmount(o.account.plan.lastPaymentAmount)}
                          </div>
                        )}
                      </>
                    ) : (
                      <span className="text-muted-foreground">–</span>
                    )}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="text-muted-foreground px-3 py-6 text-center"
                  >
                    Ingen anlegg matcher filteret.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <Dialog
        open={selected !== null}
        onOpenChange={(open) => !open && setSelected(null)}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selected?.site.name}</DialogTitle>
          </DialogHeader>
          {selected && (
            <CustomerDetail overview={selected} onSaved={refetch} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

function BackfillReport({
  results,
  onClose
}: {
  results: BackfillResult[]
  onClose: () => void
}) {
  const counts = results.reduce(
    (acc, r) => {
      acc[r.status] = (acc[r.status] || 0) + 1
      return acc
    },
    {} as Record<string, number>
  )
  const interesting = results.filter(
    (r) => r.status === 'updated' || r.status === 'error'
  )
  return (
    <div className="bg-muted/30 space-y-3 rounded-md border p-3 text-sm">
      <div className="flex items-center justify-between">
        <div>
          <strong>Stripe backfill ferdig.</strong>{' '}
          <span className="text-muted-foreground">
            {Object.entries(counts)
              .map(([k, v]) => `${v} ${k}`)
              .join(' · ')}
          </span>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground text-xs"
        >
          Lukk
        </button>
      </div>
      {interesting.length > 0 && (
        <div className="max-h-48 space-y-1 overflow-y-auto text-xs">
          {interesting.map((r) => (
            <div key={r.siteId} className="flex justify-between gap-3">
              <span className="font-mono">{r.siteId}</span>
              <span
                className={
                  r.status === 'error'
                    ? 'text-red-700'
                    : 'text-muted-foreground'
                }
              >
                {r.status}
                {r.message ? ` · ${r.message}` : ''}
                {r.lastPaymentAt ? ` · ${r.lastPaymentAt.slice(0, 10)}` : ''}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function FilterChip({
  active,
  onClick,
  children
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md border px-3 py-1 text-sm ${
        active
          ? 'border-primary bg-primary/5 font-medium'
          : 'border-border hover:bg-muted/50'
      }`}
    >
      {children}
    </button>
  )
}

function SortHeader({
  active,
  onClick,
  align,
  children
}: {
  active: boolean
  onClick: () => void
  align?: 'right'
  children: React.ReactNode
}) {
  return (
    <th
      className={`hover:text-foreground cursor-pointer px-3 py-2 select-none ${
        align === 'right' ? 'text-right' : 'text-left'
      } ${active ? 'text-foreground' : ''}`}
      onClick={onClick}
    >
      {children}
      {active && <span className="ml-1">↓</span>}
    </th>
  )
}

function TierBadge({ tier }: { tier?: AccountTier }) {
  if (!tier) return <span className="text-muted-foreground text-xs">–</span>
  const tone =
    tier === AccountTier.STANDARD
      ? 'bg-green-100 text-green-800'
      : tier === AccountTier.CUSTOM
        ? 'bg-blue-100 text-blue-800'
        : tier === AccountTier.FREE
          ? 'bg-amber-100 text-amber-800'
          : 'bg-muted text-muted-foreground'
  return (
    <span className={`rounded px-2 py-0.5 text-xs font-medium ${tone}`}>
      {tier}
    </span>
  )
}

function PlanStatus({ account }: { account: AccountDoc | null }) {
  if (!account)
    return <span className="text-muted-foreground text-xs">ingen konto</span>
  const now = DateTime.now()
  const trialEnd = account.trialEndsAt
    ? DateTime.fromISO(account.trialEndsAt)
    : null
  const inTrial = trialEnd?.isValid ? trialEnd > now : false
  const willEnd = account.plan.willEndAt
    ? DateTime.fromISO(account.plan.willEndAt)
    : null
  const ending = willEnd?.isValid ? willEnd > now : false
  return (
    <div className="text-xs">
      {inTrial && (
        <div className="text-amber-700">
          Prøveperiode til {fmtDate(account.trialEndsAt)}
        </div>
      )}
      {ending && (
        <div className="text-red-700">
          Avsluttes {fmtDate(account.plan.willEndAt)}
        </div>
      )}
      <div className="text-muted-foreground">
        Startet {fmtDate(account.plan.startedAt)}
      </div>
    </div>
  )
}

function CustomerDetail({
  overview,
  onSaved
}: {
  overview: CustomerOverview
  onSaved: () => Promise<void>
}) {
  const { site, account, currentUsage, previousUsage } = overview
  const [editing, setEditing] = useState(false)
  return (
    <div className="space-y-6 text-sm">
      <div className="text-muted-foreground text-xs">{site.id}</div>

      {account ? (
        editing ? (
          <AccountEditForm
            siteId={site.id}
            account={account}
            onCancel={() => setEditing(false)}
            onSaved={async () => {
              await onSaved()
              setEditing(false)
            }}
          />
        ) : (
        <Section
          title="Abonnement"
          action={
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="text-primary text-xs hover:underline"
            >
              Rediger
            </button>
          }
        >
          <KV label="Tier">
            <TierBadge tier={account.plan.tier} />
          </KV>
          <KV label="Startet">{fmtDate(account.plan.startedAt)}</KV>
          <KV label="Avsluttes">
            {account.plan.willEndAt ? fmtDate(account.plan.willEndAt) : '–'}
          </KV>
          <KV label="Prøveperiode">{fmtDate(account.trialEndsAt)}</KV>
          <KV label="Anlegg opprettet">{fmtDate(account.siteCreatedAt)}</KV>
          <KV label="Ordrekreditt">{account.plan.ordersCredit}</KV>
          <KV label="Provider">{account.plan.paymentProvider || '–'}</KV>
          {account.plan.paymentProvider === 'stripe' && (
            <>
              <KV label="Stripe customer">
                <StripeLink
                  type="customers"
                  id={account.plan.paymentCustomerId}
                />
              </KV>
              <KV label="Stripe subscription">
                <StripeLink
                  type="subscriptions"
                  id={account.plan.paymentSubscriptionId}
                />
              </KV>
              <KV label="Siste betaling">
                {account.plan.lastPaymentAt
                  ? `${fmtDate(account.plan.lastPaymentAt)} · ${fmtAmount(account.plan.lastPaymentAmount)}`
                  : '–'}
              </KV>
            </>
          )}
        </Section>
        )
      ) : (
        <p className="text-muted-foreground">Ingen kontodokument funnet.</p>
      )}

      {currentUsage && (
        <Section title="Bruk denne måneden">
          <UsageBlock usage={currentUsage} />
        </Section>
      )}
      {previousUsage && (
        <Section title="Bruk forrige måned">
          <UsageBlock usage={previousUsage} />
        </Section>
      )}

      {account && account.history.length > 0 && (
        <Section title={`Planhistorikk (${account.history.length})`}>
          <div className="space-y-1 text-xs">
            {account.history
              .slice(-5)
              .reverse()
              .map((h, i) => (
                <div
                  key={i}
                  className="text-muted-foreground flex justify-between gap-2"
                >
                  <span>{h.tier}</span>
                  <span>
                    {fmtDate(h.startedAt)}
                    {h.willEndAt ? ` → ${fmtDate(h.willEndAt)}` : ''}
                  </span>
                </div>
              ))}
          </div>
        </Section>
      )}
    </div>
  )
}

interface AccountPatch {
  trialEndsAt?: string
  plan?: {
    tier?: string
    startedAt?: string
    willEndAt?: string
    ordersCredit?: number
  }
}

function AccountEditForm({
  siteId,
  account,
  onCancel,
  onSaved
}: {
  siteId: string
  account: AccountDoc
  onCancel: () => void
  onSaved: () => Promise<void>
}) {
  const [tier, setTier] = useState<AccountTier>(account.plan.tier)
  const [trialEndsAt, setTrialEndsAt] = useState(isoToDateInput(account.trialEndsAt))
  const [startedAt, setStartedAt] = useState(isoToDateInput(account.plan.startedAt))
  const [willEndAt, setWillEndAt] = useState(isoToDateInput(account.plan.willEndAt))
  const [ordersCredit, setOrdersCredit] = useState(
    String(account.plan.ordersCredit)
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const tierChanged = tier !== account.plan.tier

  const save = async () => {
    setError(null)
    const patch: AccountPatch = {}
    if (trialEndsAt !== isoToDateInput(account.trialEndsAt)) {
      patch.trialEndsAt = dateInputToIso(trialEndsAt)
    }
    const plan: NonNullable<AccountPatch['plan']> = {}
    if (tierChanged) plan.tier = tier
    if (startedAt !== isoToDateInput(account.plan.startedAt)) {
      plan.startedAt = dateInputToIso(startedAt)
    }
    if (willEndAt !== isoToDateInput(account.plan.willEndAt)) {
      plan.willEndAt = dateInputToIso(willEndAt)
    }
    const creditNum = Number(ordersCredit)
    if (
      Number.isFinite(creditNum) &&
      creditNum !== account.plan.ordersCredit
    ) {
      plan.ordersCredit = creditNum
    }
    if (Object.keys(plan).length > 0) patch.plan = plan
    if (Object.keys(patch).length === 0) {
      onCancel()
      return
    }
    setSaving(true)
    try {
      const res = await apiFetch(`/admin/account/update/${siteId}`, {
        method: 'POST',
        body: JSON.stringify(patch)
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) {
        throw new Error(json?.detail || json?.title || `HTTP ${res.status}`)
      }
      await onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Section title="Rediger abonnement">
      <div className="space-y-2">
        <EditRow label="Tier">
          <select
            value={tier}
            onChange={(e) => setTier(e.target.value as AccountTier)}
            className="border-border rounded border px-2 py-1 text-sm"
          >
            {(Object.values(AccountTier) as AccountTier[]).map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          {tierChanged && (
            <span className="text-muted-foreground ml-2 text-xs">
              Nåværende plan flyttes til historikk.
            </span>
          )}
        </EditRow>
        <EditRow label="Startet">
          <DateInput value={startedAt} onChange={setStartedAt} />
        </EditRow>
        <EditRow label="Avsluttes">
          <DateInput value={willEndAt} onChange={setWillEndAt} />
        </EditRow>
        <EditRow label="Prøveperiode">
          <DateInput value={trialEndsAt} onChange={setTrialEndsAt} />
        </EditRow>
        <EditRow label="Ordrekreditt">
          <input
            type="number"
            value={ordersCredit}
            onChange={(e) => setOrdersCredit(e.target.value)}
            className="border-border w-24 rounded border px-2 py-1 text-sm"
          />
        </EditRow>
      </div>
      {error && <div className="mt-2 text-xs text-red-700">{error}</div>}
      <div className="mt-3 flex gap-2">
        <Button size="sm" onClick={save} disabled={saving}>
          {saving ? 'Lagrer...' : 'Lagre'}
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel} disabled={saving}>
          Avbryt
        </Button>
      </div>
    </Section>
  )
}

function EditRow({
  label,
  children
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="grid grid-cols-[minmax(140px,180px)_1fr] items-center gap-2">
      <div className="text-muted-foreground text-sm">{label}</div>
      <div>{children}</div>
    </div>
  )
}

function DateInput({
  value,
  onChange
}: {
  value: string
  onChange: (v: string) => void
}) {
  return (
    <div className="flex items-center gap-2">
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="border-border rounded border px-2 py-1 text-sm"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          className="text-muted-foreground hover:text-foreground text-xs"
        >
          fjern
        </button>
      )}
    </div>
  )
}

function isoToDateInput(iso: string): string {
  if (!iso) return ''
  const dt = DateTime.fromISO(iso)
  return dt.isValid ? dt.toFormat('yyyy-MM-dd') : ''
}

// Store dates as end-of-day so a trial "until 2026-08-31" is still valid all
// that day. Empty input clears the field.
function dateInputToIso(v: string): string {
  if (!v) return ''
  const dt = DateTime.fromISO(v).endOf('day')
  return dt.isValid ? (dt.toISO() ?? '') : ''
}

function UsageBlock({ usage }: { usage: UsageDoc }) {
  return (
    <>
      <KV label="Ordre">
        {usage.orders.completed.count} ·{' '}
        {fmtAmount(usage.orders.completed.amount)}
      </KV>
      <KV label="Unike brukere">{usage.uniqueUserIds.length}</KV>
      <KV label="Arrangementer (oms.)">{fmtAmount(usage.events.amount)}</KV>
      <KV label="Gavekort solgt">
        {usage.giftCards.bought.count} ·{' '}
        {fmtAmount(usage.giftCards.bought.amount)}
      </KV>
      <KV label="Siste ordre">{fmtDate(usage.lastOrderDate)}</KV>
      <KV label="Oppdatert">{fmtDate(usage.lastUpdated)}</KV>
    </>
  )
}

function Section({
  title,
  action,
  children
}: {
  title: string
  action?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="font-semibold">{title}</h3>
        {action}
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  )
}

function KV({
  label,
  children
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="grid grid-cols-[minmax(140px,180px)_1fr] gap-2">
      <div className="text-muted-foreground">{label}</div>
      <div>{children}</div>
    </div>
  )
}

function StripeLink({
  type,
  id
}: {
  type: 'customers' | 'subscriptions'
  id: string
}) {
  if (!id) return <span className="text-muted-foreground">–</span>
  return (
    <a
      href={`https://dashboard.stripe.com/${type}/${id}`}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary font-mono text-xs hover:underline"
    >
      {id}
    </a>
  )
}

function fmtAmount(n?: number): string {
  if (n === undefined || n === null) return '–'
  return `${n.toLocaleString('nb-NO', { maximumFractionDigits: 0 })} kr`
}

function fmtDate(iso?: string): string {
  if (!iso) return '–'
  const dt = DateTime.fromISO(iso).setLocale('nb-NO')
  if (!dt.isValid) return iso
  return dt.toFormat('dd.MM.yyyy')
}
